// Tab: Arhivă stoc lunar — monthly inventory snapshots for accounting.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FileSpreadsheet, FileText, Loader2, Calendar, Trash2, Eye, ArrowLeft,
  Search, X, Plus, Coins, Archive, ImageOff, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  listSnapshots, generateSnapshot, getSnapshot, deleteSnapshot,
  downloadSnapshotXlsx, downloadSnapshotPdf, listSuppliers,
} from "@/lib/inventory";
import { fmtDate } from "@/lib/format";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

const STATUS_FILTERS = [
  { id: "all", label: "Toate" },
  { id: "active", label: "Active" },
  { id: "draft", label: "Draft" },
  { id: "archived", label: "Arhivate" },
  { id: "high_value", label: "Top valoare", icon: Coins },
];

const STATUS_BADGES = {
  ACTIVE: { label: "Active", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  DRAFT: { label: "Draft", className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
  ARCHIVED: { label: "Arhiv", className: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
};


export default function SnapshotsTab() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently viewed snapshot id
  const [generating, setGenerating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setSnapshots(await listSnapshots()); }
    catch { toast.error("Eroare la încărcare"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const generateCurrent = async () => {
    // Use Europe/Bucharest to determine the current year/month
    const ro = new Date().toLocaleString("en-CA", { timeZone: "Europe/Bucharest", year: "numeric", month: "2-digit", day: "2-digit" });
    const [y, m] = ro.split("-");
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    if (!window.confirm(`Generezi snapshot pentru luna curentă (${MONTHS_RO[month - 1]} ${year})?`)) return;
    setGenerating(true);
    const tid = toast.loading("Generez snapshot...");
    try {
      await generateSnapshot(year, month);
      toast.success("Snapshot generat", { id: tid });
      await reload();
    } catch {
      toast.error("Eroare", { id: tid });
    } finally { setGenerating(false); }
  };

  const regenerate = async (snap) => {
    if (!window.confirm(`Regenerezi snapshot pentru ${MONTHS_RO[snap.month - 1]} ${snap.year}? Datele actuale vor fi înlocuite.`)) return;
    setGenerating(true);
    const tid = toast.loading("Regenerez...");
    try {
      await generateSnapshot(snap.year, snap.month);
      toast.success("Snapshot regenerat", { id: tid });
      await reload();
    } catch {
      toast.error("Eroare", { id: tid });
    } finally { setGenerating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi acest snapshot?")) return;
    try {
      await deleteSnapshot(id);
      await reload();
      toast.success("Snapshot șters");
    } catch { toast.error("Eroare"); }
  };

  if (active) {
    return <SnapshotDetail id={active} onBack={() => setActive(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="snapshots-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Archive className="h-5 w-5 text-primary" />
            Arhivă stoc lunar
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Snapshot-uri lunare cu valoare stoc — gata de trimis contabilei (PDF + Excel).
          </p>
        </div>
        <Button size="sm" onClick={generateCurrent} disabled={generating} data-testid="snapshot-generate-btn">
          {generating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
          Generează acum
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : snapshots.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Calendar className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">Niciun snapshot încă.</div>
          <div className="text-xs mt-1">Sistemul generează automat ultima zi a lunii la ora 20:00 (Europe/Bucharest) — sau apasă „Generează acum".</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {snapshots.map((s) => (
            <Card key={s.id} className="p-4 hover:shadow-md transition group" data-testid={`snapshot-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base">
                    {MONTHS_RO[s.month - 1]} {s.year}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Generat {fmtDate(s.created_at)} · {s.actor_email}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                  {s.status}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <KV label="Produse" value={s.total_skus} />
                <KV label="Bucăți fizice" value={s.total_qty} mono />
                <KV label="Valoare RON" value={s.total_value} mono />
              </div>
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border/60">
                <Button size="sm" variant="outline" className="h-8 flex-1" onClick={() => setActive(s.id)} data-testid={`snapshot-view-${s.id}`}>
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Vezi
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => downloadSnapshotXlsx(s.id, s.period).catch(() => toast.error("Eroare Excel"))} data-testid={`snapshot-xlsx-${s.id}`} title="Excel">
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => downloadSnapshotPdf(s.id, s.period).catch(() => toast.error("Eroare PDF"))} data-testid={`snapshot-pdf-${s.id}`} title="PDF">
                  <FileText className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-foreground opacity-60 group-hover:opacity-100" onClick={() => regenerate(s)} title="Regenerează" data-testid={`snapshot-regen-${s.id}`}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-red-500 opacity-60 group-hover:opacity-100" onClick={() => remove(s.id)} title="Șterge" data-testid={`snapshot-del-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm font-semibold ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}


function SnapshotDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (search) params.search = search;
      if (supplierId) params.supplier_id = supplierId;
      const d = await getSnapshot(id, params);
      setData(d);
    } catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  }, [id, search, status, supplierId]);
  useEffect(() => {
    const t = setTimeout(reload, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [reload, search]);
  useEffect(() => { listSuppliers().then(setSuppliers).catch(() => {}); }, []);

  return (
    <div className="space-y-3" data-testid="snapshot-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="snapshot-back">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Înapoi la arhivă
          </Button>
          {data && (
            <div className="font-display font-semibold text-base">
              {MONTHS_RO[data.month - 1]} {data.year}
              <span className="text-[11px] text-muted-foreground font-normal ml-2">
                · {data.total_skus} SKU · {data.total_qty} buc · {data.total_value} RON
              </span>
            </div>
          )}
        </div>
        {data && (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-8" onClick={() => downloadSnapshotXlsx(data.id, data.period).catch(() => toast.error("Eroare Excel"))}>
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => downloadSnapshotPdf(data.id, data.period).catch(() => toast.error("Eroare PDF"))}>
              <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută SKU, titlu, furnizor..."
            className="pl-9 pr-8 h-9 bg-secondary/40"
            data-testid="snapshot-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={supplierId || "ALL"} onValueChange={(v) => setSupplierId(v === "ALL" ? "" : v)}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="snapshot-supplier-filter">
            <SelectValue placeholder="Toți furnizorii" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toți furnizorii</SelectItem>
            <SelectItem value="none">Fără furnizor</SelectItem>
            {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatus(f.id)}
              data-testid={`snapshot-filter-${f.id}`}
              className={`text-[11px] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                status === f.id ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f.icon ? <f.icon className="h-3 w-3" /> : null}
              {f.label}
            </button>
          ))}
        </div>
        {data && (
          <div className="ml-auto text-[11px] text-muted-foreground self-center pr-2">
            {data.filtered_count} / {data.total_skus} produse
          </div>
        )}
      </div>

      {/* Table */}
      {loading || !data ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <div className="overflow-x-auto max-h-[calc(100vh-26rem)] overflow-y-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-secondary/40 backdrop-blur">
                <tr>
                  {["", "Produs", "SKU", "Furnizor", "Cantitate fizică", "Cost unitar", "Valoare totală", "Status"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="snapshot-rows">
                {(data.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                      Niciun produs nu corespunde filtrelor selectate.
                    </td>
                  </tr>
                ) : (data.items || []).map((it, i) => {
                  const sb = STATUS_BADGES[(it.status || "ACTIVE").toUpperCase()] || STATUS_BADGES.ACTIVE;
                  return (
                    <tr key={i} className="border-b border-border/40 hover:bg-secondary/20">
                      <td className="px-3 py-2 w-10">
                        <div className="h-8 w-8 rounded bg-secondary overflow-hidden flex items-center justify-center">
                          {it.image ? (
                            <img src={it.image} alt="" loading="lazy" className="h-full w-full object-cover" />
                          ) : <ImageOff className="h-3 w-3 text-muted-foreground" />}
                        </div>
                      </td>
                      <td className="px-3 py-2 max-w-[420px]">
                        <div className="truncate">{it.title}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{it.sku || "—"}</td>
                      <td className="px-3 py-2 text-xs">{it.supplier_name || <span className="text-muted-foreground/50">—</span>}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-foreground">{it.warehouse_qty}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">{it.cost}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{it.stock_value}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${sb.className}`}>{sb.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-secondary/30 font-semibold">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right text-xs uppercase tracking-widest">Total</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{data.total_qty}</td>
                  <td colSpan={1}></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-primary">{data.total_value} RON</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
